import { useApiMutation, useApiQuery } from "@gladis/api-client";
import type { CurrentUserDto, UserDto } from "@gladis/shared";
import { useState, type ReactNode } from "react";
import { Avatar, Button, Label, TextField } from "@gladis/ui";
import { errorSentence, useT } from "@gladis/ui/i18n";

import { AvatarPicker } from "./AvatarPicker.js";

/**
 * Display name and email address (FR-029, FR-030).
 *
 * Two changes with genuinely different mechanics, and the interface says so rather than presenting
 * them as one form: a name changes immediately, an address changes only when a link is followed.
 */
export function ProfileSettings(): ReactNode {
  const t = useT();
  const me = useApiQuery<CurrentUserDto>(["me"], "/me");
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState("");
  const [emailRequested, setEmailRequested] = useState(false);
  const [picking, setPicking] = useState(false);

  /*
    Both invalidate `["me"]` AND `["members"]`. The member list is what carries a face to every
    other page in the application, so invalidating only `["me"]` would update this page and leave
    the savings table showing the old picture until its own staleness window expired.
  */
  const setPicture = useApiMutation<UserDto, { image: string }>(
    (client, input) => client.put<UserDto>("/me/avatar", input),
    { invalidates: [["me"], ["members"]], onSuccess: () => setPicking(false) },
  );

  const removePicture = useApiMutation<UserDto, undefined>(
    (client) => client.delete<UserDto>("/me/avatar"),
    { invalidates: [["me"], ["members"]] },
  );

  const rename = useApiMutation<UserDto, { displayName: string }>(
    (client, input) => client.patch<UserDto>("/me", input),
    { invalidates: [["me"]] },
  );

  const changeEmail = useApiMutation<void, { email: string }>(
    (client, input) => client.post("/me/email-change", input),
    { onSuccess: () => setEmailRequested(true) },
  );

  if (!me.data) return <p className="text-muted">{t("identity.loading")}</p>;

  // Uncontrolled until first edit, so the field shows the current value without a round trip.
  const nameValue = displayName ?? me.data.user.displayName;

  return (
    <section className="rounded-control border border-line bg-panel p-5">
      <h2 className="text-xl font-semibold">{t("identity.profile.heading")}</h2>

      {/*
        The picture, above the name, because it is the thing a member came here to change and the
        name field has been on this page since 003.

        THE PICKER IS MOUNTED ONLY WHILE OPEN. Left mounted it would keep the `useState` from the
        first time it was opened — the previous file, zoom and offset — so a second attempt would
        start half-way through the first one. 016's dialogs learned this the same way.
      */}
      <div className="mt-4 flex items-center gap-4">
        <Avatar
          src={me.data.user.avatarUrl}
          name={me.data.user.displayName}
          size="lg"
          testId="my-avatar"
        />
        <div>
          {picking ? null : (
            <div className="flex gap-2">
              <Button onClick={() => setPicking(true)} data-testid="avatar-change">
                {me.data.user.avatarUrl === null
                  ? t("identity.profile.addPicture")
                  : t("identity.profile.changePicture")}
              </Button>
              {me.data.user.avatarUrl === null ? null : (
                <Button
                  onClick={() => removePicture.mutate(undefined)}
                  disabled={removePicture.isPending}
                  data-testid="avatar-remove"
                >
                  {removePicture.isPending ? t("common.removing") : t("common.remove")}
                </Button>
              )}
            </div>
          )}
          <p className="mt-2 text-sm text-muted">{t("identity.profile.pictureVisibility")}</p>
        </div>
      </div>

      {picking ? (
        <AvatarPicker
          busy={setPicture.isPending}
          onCancel={() => setPicking(false)}
          onChoose={(image) => setPicture.mutate({ image })}
        />
      ) : null}

      {setPicture.isError ? (
        <p className="mt-3 text-sm text-danger" role="alert">
          {errorSentence(setPicture.error)}
        </p>
      ) : null}

      <form
        className="mt-4"
        onSubmit={(event) => {
          event.preventDefault();
          rename.mutate({ displayName: nameValue.trim() });
        }}
      >
        <Label htmlFor="display-name">{t("identity.profile.displayNameLabel")}</Label>
        <TextField
          id="display-name"
          value={nameValue}
          onChange={(event) => setDisplayName(event.target.value)}
          required
        />
        <Button
          className="mt-4"
          type="submit"
          disabled={rename.isPending || nameValue.trim().length === 0}
        >
          {rename.isPending ? t("common.saving") : t("identity.profile.saveName")}
        </Button>
        {/* Says what does NOT change, because that is the surprising part. */}
        <p className="mt-3 text-sm text-muted">{t("identity.profile.displayNameHint")}</p>
      </form>

      {rename.isError ? (
        <p className="mt-4 text-sm text-muted" role="alert">
          {errorSentence(rename.error)}
        </p>
      ) : null}

      <h3 className="mt-8 text-lg font-semibold">{t("identity.profile.emailAddress")}</h3>
      <p className="mt-1 text-muted">Currently {me.data.user.email}.</p>

      {emailRequested ? (
        <p className="mt-3 text-muted" role="status">
          We&apos;ve sent a confirmation link to {newEmail}. Your address stays as{" "}
          {me.data.user.email} until you follow it.
        </p>
      ) : (
        <form
          className="mt-4"
          onSubmit={(event) => {
            event.preventDefault();
            changeEmail.mutate({ email: newEmail.trim() });
          }}
        >
          <Label htmlFor="new-email">{t("identity.profile.newEmail")}</Label>
          <TextField
            id="new-email"
            type="email"
            value={newEmail}
            onChange={(event) => setNewEmail(event.target.value)}
            required
          />
          <Button
            className="mt-4"
            type="submit"
            disabled={changeEmail.isPending || newEmail.trim().length === 0}
          >
            {changeEmail.isPending ? t("common.sending") : t("identity.profile.sendConfirmation")}
          </Button>
        </form>
      )}

      {changeEmail.isError ? (
        <p className="mt-4 text-sm text-muted" role="alert">
          {errorSentence(changeEmail.error)}
        </p>
      ) : null}
    </section>
  );
}
